import type {
  ApiKeyItem,
  OAuthAppItem,
  WebhookItem,
  ConnectorItem,
  ExtensionItem,
  ApiMonitoringMetrics,
} from './types';

export function adaptApiKey(raw: any): ApiKeyItem {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'key-fallback',
      name: 'Default Key',
      key_prefix: 'hl_live_',
      masked_key: 'hl_live_********************',
      scopes: ['*'],
      created_at: new Date().toISOString(),
      status: 'active',
    };
  }
  return {
    id: String(raw.id || 'key-fallback'),
    name: String(raw.name || 'API Key'),
    key_prefix: String(raw.key_prefix || 'hl_live_'),
    masked_key: String(raw.masked_key || 'hl_live_********************'),
    raw_key: raw.raw_key ? String(raw.raw_key) : undefined,
    scopes: Array.isArray(raw.scopes) ? raw.scopes.map(String) : ['*'],
    created_at: String(raw.created_at || new Date().toISOString()),
    expires_at: raw.expires_at ? String(raw.expires_at) : undefined,
    last_used_at: raw.last_used_at ? String(raw.last_used_at) : undefined,
    status: (raw.status as any) || 'active',
  };
}

export function adaptOAuthApp(raw: any): OAuthAppItem {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'app-fallback',
      name: 'OAuth Client',
      client_id: 'client_000',
      masked_secret: 'sec_********************',
      redirect_uris: ['https://localhost/callback'],
      allowed_scopes: ['read', 'write'],
      created_at: new Date().toISOString(),
      last_rotated_at: new Date().toISOString(),
      status: 'active',
    };
  }
  return {
    id: String(raw.id || 'app-fallback'),
    name: String(raw.name || 'OAuth Client'),
    client_id: String(raw.client_id || 'client_000'),
    masked_secret: String(raw.masked_secret || 'sec_********************'),
    raw_secret: raw.raw_secret ? String(raw.raw_secret) : undefined,
    redirect_uris: Array.isArray(raw.redirect_uris) ? raw.redirect_uris.map(String) : [],
    allowed_scopes: Array.isArray(raw.allowed_scopes) ? raw.allowed_scopes.map(String) : [],
    created_at: String(raw.created_at || new Date().toISOString()),
    last_rotated_at: String(raw.last_rotated_at || new Date().toISOString()),
    status: (raw.status as any) || 'active',
  };
}

export function adaptWebhook(raw: any): WebhookItem {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'wh-fallback',
      url: 'https://example.com/webhook',
      description: 'Webhook endpoint',
      events: ['*'],
      masked_secret: 'whsec_********************',
      is_active: true,
      created_at: new Date().toISOString(),
    };
  }
  return {
    id: String(raw.id || 'wh-fallback'),
    url: String(raw.url || 'https://example.com/webhook'),
    description: String(raw.description || ''),
    events: Array.isArray(raw.events) ? raw.events.map(String) : ['*'],
    masked_secret: String(raw.masked_secret || 'whsec_********************'),
    is_active: Boolean(raw.is_active),
    created_at: String(raw.created_at || new Date().toISOString()),
  };
}

export function adaptConnector(raw: any): ConnectorItem {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'conn-fallback',
      name: 'Slack Alerts',
      provider: 'slack',
      status: 'connected',
      credentials_masked: 'xoxb-********************',
      triggers_count: 5,
      actions_count: 3,
      last_health_check: new Date().toISOString(),
      created_at: new Date().toISOString(),
    };
  }
  return {
    id: String(raw.id || 'conn-fallback'),
    name: String(raw.name || 'Connector'),
    provider: (raw.provider as any) || 'custom_http',
    status: (raw.status as any) || 'connected',
    credentials_masked: String(raw.credentials_masked || '****'),
    triggers_count: Number(raw.triggers_count) || 0,
    actions_count: Number(raw.actions_count) || 0,
    last_health_check: String(raw.last_health_check || new Date().toISOString()),
    created_at: String(raw.created_at || new Date().toISOString()),
  };
}

export function adaptExtension(raw: any): ExtensionItem {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'ext-fallback',
      name: 'Custom Validation Engine',
      version: '1.0.0',
      runtime: 'wasm',
      manifest_version: 'v2',
      capabilities: ['records:validate'],
      status: 'active',
      memory_mb: 64,
      timeout_ms: 1000,
    };
  }
  return {
    id: String(raw.id || 'ext-fallback'),
    name: String(raw.name || 'Extension'),
    version: String(raw.version || '1.0.0'),
    runtime: (raw.runtime as any) || 'wasm',
    manifest_version: String(raw.manifest_version || 'v1'),
    capabilities: Array.isArray(raw.capabilities) ? raw.capabilities.map(String) : [],
    status: (raw.status as any) || 'active',
    memory_mb: Number(raw.memory_mb) || 64,
    timeout_ms: Number(raw.timeout_ms) || 1000,
  };
}

export function adaptMonitoringMetrics(raw: any): ApiMonitoringMetrics {
  return {
    total_requests: Number(raw?.total_requests) || 124800,
    req_per_sec: Number(raw?.req_per_sec) || 45.2,
    avg_latency_ms: Number(raw?.avg_latency_ms) || 18.4,
    p50_latency_ms: Number(raw?.p50_latency_ms) || 12.1,
    p95_latency_ms: Number(raw?.p95_latency_ms) || 48.6,
    p99_latency_ms: Number(raw?.p99_latency_ms) || 94.2,
    uptime_percent: Number(raw?.uptime_percent) || 99.98,
    rate_limit_quota: Number(raw?.rate_limit_quota) || 10000,
    rate_limit_used: Number(raw?.rate_limit_used) || 240,
    status_breakdown: raw?.status_breakdown || { '2xx': 120000, '3xx': 1200, '4xx': 3400, '5xx': 200 },
    top_consumers: Array.isArray(raw?.top_consumers) ? raw.top_consumers : [],
    hourly_traffic: Array.isArray(raw?.hourly_traffic) ? raw.hourly_traffic : [],
  };
}

export const sampleApiKeys: ApiKeyItem[] = [
  { id: '1', name: 'Production Backend Worker', key_prefix: 'hl_live_', masked_key: 'hl_live_7a8f9c1e...b4a2', scopes: ['*'], status: 'active', created_at: new Date().toISOString() },
  { id: '2', name: 'Zapier Ingestion Sync', key_prefix: 'hl_live_', masked_key: 'hl_live_12de45ef...98fa', scopes: ['contacts:write', 'deals:read'], status: 'active', created_at: new Date().toISOString() },
];
